/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Stack,
    Box,
    Typography,
    Chip,
    Avatar,
    Autocomplete,
    CircularProgress,
    IconButton,
    Switch,
    FormControlLabel,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from 'src/components/Iconify';
import TaskMergeTagPicker from 'src/components/TaskMergeTagPicker';
import { dispatch, useSelector } from 'src/redux/store';
import { createTask } from 'src/redux/slices/taskSlice';
import { TASK_PRIORITY, TASK_CATEGORY, TASK_METHOD } from 'src/constants/TaskConstants';
import { callApi } from 'src/utils/apiCall';
import { toDatetimeLocalString } from 'src/utils/taskDateUtils';
import { common } from 'src/theme/palette';

// -----------------------------------------------

const schema = Yup.object().shape({
    title: Yup.string().trim().required('Title is required'),
    description: Yup.string().nullable(),
    due_at: Yup.string().required('Due date/time is required'),
    priority: Yup.string().required(),
    category: Yup.string().required(),
    method: Yup.string().nullable(),
});

const PRIORITY_OPTIONS = [
    { label: 'Urgent', value: TASK_PRIORITY.URGENT },
    { label: 'High', value: TASK_PRIORITY.HIGH },
    { label: 'Normal', value: TASK_PRIORITY.NORMAL },
    { label: 'Low', value: TASK_PRIORITY.LOW },
];

const CATEGORY_OPTIONS = [
    { label: 'New Lead', value: TASK_CATEGORY.NEW_LEAD },
    { label: 'Follow Up', value: TASK_CATEGORY.FOLLOW_UP },
    { label: 'Lead & Marketing', value: TASK_CATEGORY.LEAD_MARKETING },
    { label: 'Patient Care', value: TASK_CATEGORY.PATIENT_CARE },
    { label: 'Clinical', value: TASK_CATEGORY.CLINICAL },
    { label: 'Operational', value: TASK_CATEGORY.OPERATIONAL },
    { label: 'Compliance', value: TASK_CATEGORY.COMPLIANCE },
];

const METHOD_OPTIONS = [
    { label: 'Call', value: TASK_METHOD.CALL },
    { label: 'Email', value: TASK_METHOD.EMAIL },
    { label: 'WhatsApp', value: TASK_METHOD.WHATSAPP },
    { label: 'Payment', value: TASK_METHOD.PAYMENT },
    { label: 'Other', value: TASK_METHOD.OTHER },
];

// -----------------------------------------------

// #231 — KPI empty-state CTA → pre-fill due_at when the dialog opens. Centralised
// here so any caller (KPI CTA, slash-command, etc) can request a sensible default.
const computeDefaultDue = (defaultDue) => {
    if (!defaultDue) return '';
    const d = new Date();
    if (defaultDue === 'today') {
        d.setHours(d.getHours() + 1, 0, 0, 0);
    } else if (defaultDue === 'week') {
        const daysUntilMon = ((8 - d.getDay()) % 7) || 7;
        d.setDate(d.getDate() + daysUntilMon);
        d.setHours(9, 0, 0, 0);
    } else {
        return '';
    }
    // ``toDatetimeLocalString`` renders local components directly. The previous
    // ``d.toISOString().slice(0, 16)`` converted to UTC first and stripped the
    // ``Z``, so the widget read the result as local — a one-offset shift in
    // BST (and zero shift in GMT). See src/utils/taskDateUtils.js.
    return toDatetimeLocalString(d);
};

export default function CreateTaskDialog({ open, onClose, practiceId, onSuccess, defaultLead, defaultDue, defaultTitle, defaultDescription }) {
    const [submitting, setSubmitting] = useState(false);
    const titleFieldRef = useRef(null);
    const descFieldRef = useRef(null);
    const [selectedAssignees, setSelectedAssignees] = useState([]);
    const [selectedLead, setSelectedLead] = useState(defaultLead || null);
    const [leadInputValue, setLeadInputValue] = useState('');
    const [leadOptions, setLeadOptions] = useState([]);
    const [leadLoading, setLeadLoading] = useState(false);
    const [checklistItems, setChecklistItems] = useState([]);
    const [selectedTreatmentId, setSelectedTreatmentId] = useState(null);

    const practiceUsers = useSelector((state) => state.practiceUser.practiceUser.results || []);

    // Filter out ADMIN, SUPPORT role users, superusers, and disabled accounts from assignees list.
    // ADMIN and SUPPORT are internal roles that must not be exposed in user-facing dropdowns
    // (mirrors the security boundary in src/utils/filterAssigneeList.js).
    const assigneeOptions = practiceUsers.filter(
        (pu) => pu.role !== 'ADMIN' && pu.role !== 'SUPPORT' && pu.user && !pu.user.is_superuser && pu.is_enabled
    );

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            title: defaultTitle || '',
            description: defaultDescription || '',
            due_at: '',
            priority: TASK_PRIORITY.NORMAL,
            category: TASK_CATEGORY.OPERATIONAL,
            method: '',
        },
    });

    const [leadTreatments, setLeadTreatments] = useState([]);

    // Fetch treatments when lead changes (search results may not include lead_treatments)
    useEffect(() => {
        if (!selectedLead) {
            setLeadTreatments([]);
            setSelectedTreatmentId(null);
            return;
        }
        // Use lead_treatments from search result if present; otherwise fetch
        if (Array.isArray(selectedLead.lead_treatments) && selectedLead.lead_treatments.length > 0) {
            setLeadTreatments(selectedLead.lead_treatments);
        } else {
            callApi(`/api/v1/practices/${practiceId}/leads/${selectedLead.id}/`)
                .then((res) => {
                    if (res.status === 200) {
                        setLeadTreatments(res.data?.lead_treatments || []);
                    }
                });
        }
    }, [selectedLead, practiceId]);

    // Auto-select single treatment when treatments load
    useEffect(() => {
        if (leadTreatments.length === 1) {
            setSelectedTreatmentId(leadTreatments[0].id);
        } else {
            setSelectedTreatmentId(null);
        }
    }, [leadTreatments]);

    // Reset form state when dialog opens/closes
    useEffect(() => {
        if (open) {
            const prefilledDue = computeDefaultDue(defaultDue);
            reset({
                title: defaultTitle || '',
                description: defaultDescription || '',
                due_at: prefilledDue,
                priority: TASK_PRIORITY.NORMAL,
                category: TASK_CATEGORY.OPERATIONAL,
                method: '',
            });
            setSelectedAssignees([]);
            setSelectedLead(defaultLead || null);
            setLeadInputValue('');
            setLeadOptions([]);
            setChecklistItems([]);
            setSubmitError(null);
            setSelectedTreatmentId(null);
            setLeadTreatments([]);
        }
    }, [open]);

    // Lead search with debounce
    useEffect(() => {
        if (!leadInputValue || leadInputValue.length < 2) {
            setLeadOptions([]);
            return;
        }
        let active = true;
        setLeadLoading(true);
        callApi(
            `/api/v1/practices/${practiceId}/leads/?search=${encodeURIComponent(leadInputValue)}&limit=10`
        )
            .then((res) => {
                if (active && res.status === 200) {
                    setLeadOptions(res.data?.results || []);
                }
            })
            .finally(() => {
                if (active) setLeadLoading(false);
            });
        // eslint-disable-next-line consistent-return
        return () => {
            active = false;
        };
    }, [leadInputValue, practiceId]);

    const addChecklistItem = useCallback(() => {
        setChecklistItems((prev) => [...prev, { title: '', is_required: false }]);
    }, []);

    const removeChecklistItem = useCallback((index) => {
        setChecklistItems((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const updateChecklistItem = useCallback((index, field, value) => {
        setChecklistItems((prev) =>
            prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
        );
    }, []);

    const [submitError, setSubmitError] = useState(null);

    const onSubmit = (formData) => {
        setSubmitting(true);
        setSubmitError(null);

        const payload = {
            title: formData.title.trim(),
            description: formData.description || '',
            due_at: formData.due_at,
            priority: formData.priority,
            category: formData.category,
            ...(formData.method && { method: formData.method }),
            ...(selectedAssignees.length > 0 && {
                assignees: selectedAssignees.map((a) => ({ practice_user_id: a.id })),
            }),
            ...(selectedLead && { lead: selectedLead.id }),
            ...(selectedTreatmentId && { lead_treatment: selectedTreatmentId }), // eslint-disable-line camelcase
            ...(checklistItems.length > 0 && {
                checklist_items: checklistItems.filter((item) => item.title.trim()),
            }),
        };

        try {
            dispatch(
                createTask(practiceId, payload, (result) => {
                    setSubmitting(false);
                    if (result?.success) {
                        onClose();
                        if (onSuccess) onSuccess();
                    } else {
                        setSubmitError('Failed to create task. Please try again.');
                    }
                })
            );
        } catch (e) {
            setSubmitting(false);
            setSubmitError(e.message || 'Failed to create task. Please try again.');
        }
    };

    const handleClose = () => {
        if (!submitting) onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight={700}>
                    New Task
                </Typography>
                <IconButton onClick={handleClose} size="small" disabled={submitting}>
                    <Iconify icon="eva:close-fill" width={20} />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 2 }}>
                <Stack spacing={2.5}>
                    {/* Title */}
                    <Controller
                        name="title"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Title"
                                required
                                fullWidth
                                autoFocus
                                error={!!errors.title}
                                helperText={errors.title?.message}
                                size="small"
                                ref={titleFieldRef}
                                InputProps={{
                                    endAdornment: (
                                        <TaskMergeTagPicker
                                            fieldRef={titleFieldRef}
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    ),
                                }}
                            />
                        )}
                    />

                    {/* Description */}
                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Description"
                                fullWidth
                                multiline
                                rows={3}
                                size="small"
                                placeholder="Optional description..."
                                ref={descFieldRef}
                                InputProps={{
                                    endAdornment: (
                                        <TaskMergeTagPicker
                                            fieldRef={descFieldRef}
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    ),
                                }}
                            />
                        )}
                    />

                    {/* Due Date/Time — quick-set buttons + picker */}
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>Quick set:</Typography>
                        <Stack direction="row" spacing={0.75} sx={{ mb: 1.5 }}>
                            {[
                                {
                                    label: 'Today',
                                    getValue: () => {
                                        const d = new Date();
                                        d.setHours(d.getHours() + 1, 0, 0, 0);
                                        return toDatetimeLocalString(d);
                                    },
                                },
                                {
                                    label: 'Tomorrow',
                                    getValue: () => {
                                        const d = new Date();
                                        d.setDate(d.getDate() + 1);
                                        d.setHours(9, 0, 0, 0);
                                        return toDatetimeLocalString(d);
                                    },
                                },
                                {
                                    label: 'Next Week',
                                    getValue: () => {
                                        const d = new Date();
                                        const daysUntilMon = ((8 - d.getDay()) % 7) || 7;
                                        d.setDate(d.getDate() + daysUntilMon);
                                        d.setHours(9, 0, 0, 0);
                                        return toDatetimeLocalString(d);
                                    },
                                },
                            ].map((opt) => (
                                <Button
                                    key={opt.label}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setValue('due_at', opt.getValue(), { shouldValidate: true })}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        py: 0.25,
                                        px: 1,
                                        borderColor: 'divider',
                                        color: 'text.secondary',
                                        '&:hover': { borderColor: common.mintyZest, color: common.mintyZest },
                                    }}
                                >
                                    {opt.label}
                                </Button>
                            ))}
                        </Stack>
                        <Controller
                            name="due_at"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Due Date & Time"
                                    type="datetime-local"
                                    required
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    error={!!errors.due_at}
                                    helperText={errors.due_at?.message}
                                    inputProps={{
                                        min: toDatetimeLocalString(new Date()),
                                    }}
                                />
                            )}
                        />
                    </Box>

                    {/* Priority + Category row */}
                    <Stack direction="row" spacing={2}>
                        <Controller
                            name="priority"
                            control={control}
                            render={({ field }) => (
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Priority</InputLabel>
                                    <Select {...field} label="Priority">
                                        {PRIORITY_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        <Controller
                            name="category"
                            control={control}
                            render={({ field }) => (
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Category</InputLabel>
                                    <Select {...field} label="Category">
                                        {CATEGORY_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </Stack>

                    {/* Method */}
                    <Controller
                        name="method"
                        control={control}
                        render={({ field }) => (
                            <FormControl size="small" fullWidth>
                                <InputLabel>Method (optional)</InputLabel>
                                <Select {...field} label="Method (optional)" displayEmpty>
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {METHOD_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />

                    {/* Assignees multi-select — uses Autocomplete's built-in floating label
                        to match the Title/Description/Date input pattern. */}
                    <Autocomplete
                        multiple
                        size="small"
                        options={assigneeOptions}
                        value={selectedAssignees}
                        onChange={(_, newValue) => setSelectedAssignees(newValue)}
                        getOptionLabel={(option) =>
                            `${option.user?.first_name || ''} ${option.user?.last_name || ''}`.trim()
                        }
                        isOptionEqualToValue={(option, value) =>
                            option.user?.id === value.user?.id
                        }
                        renderOption={(props, option) => (
                            <Box component="li" {...props}>
                                <Avatar
                                    src={option.user?.profile_picture || undefined}
                                    alt={option.user?.first_name}
                                    sx={{ width: 24, height: 24, mr: 1, fontSize: '0.7rem' }}
                                >
                                    {(option.user?.first_name?.[0] || '').toUpperCase()}
                                </Avatar>
                                <Typography variant="body2">
                                    {option.user?.first_name} {option.user?.last_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    {option.role}
                                </Typography>
                            </Box>
                        )}
                        renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => (
                                <Chip
                                    key={option.user?.id}
                                    avatar={
                                        <Avatar
                                            src={option.user?.profile_picture || undefined}
                                            alt={option.user?.first_name}
                                            sx={{ fontSize: '0.65rem' }}
                                        >
                                            {(option.user?.first_name?.[0] || '').toUpperCase()}
                                        </Avatar>
                                    }
                                    label={`${option.user?.first_name || ''} ${option.user?.last_name || ''}`.trim()}
                                    size="small"
                                    {...getTagProps({ index })}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Assignees (optional)"
                                size="small"
                            />
                        )}
                    />

                    {/* Lead autocomplete — floating label, no caption row */}
                    <Autocomplete
                        size="small"
                        options={leadOptions}
                        value={selectedLead}
                        onChange={(_, newValue) => setSelectedLead(newValue)}
                        onInputChange={(_, newInputValue) => setLeadInputValue(newInputValue)}
                        getOptionLabel={(option) =>
                            `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.email || ''
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        loading={leadLoading}
                        filterOptions={(x) => x}
                        noOptionsText={
                            leadInputValue.length < 2
                                ? 'Type at least 2 characters to search'
                                : 'No leads found'
                        }
                        renderOption={(props, option) => (
                            <Box component="li" {...props}>
                                <Box>
                                    <Typography variant="body2">
                                        {option.first_name} {option.last_name}
                                    </Typography>
                                    {option.email && (
                                        <Typography variant="caption" color="text.secondary">
                                            {option.email}
                                        </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Linked Lead (optional)"
                                placeholder="Search by name..."
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {leadLoading && <CircularProgress color="inherit" size={16} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {/* Treatment — single Select regardless of 1 vs many treatments,
                        so the user can always change/clear it. Matches floating-label
                        pattern of the other fields. */}
                    {leadTreatments.length > 0 && (
                        <FormControl size="small" fullWidth>
                            <InputLabel>Treatment (optional)</InputLabel>
                            <Select
                                value={selectedTreatmentId || ''}
                                onChange={(e) => setSelectedTreatmentId(e.target.value || null)}
                                label="Treatment (optional)"
                                displayEmpty
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {leadTreatments.map((lt) => {
                                    // Lead-detail API nests the human-readable name under
                                    // treatment_data.treatment_item.name (master TreatmentItem)
                                    // OR treatment_data.name (practice-specific). Fall back
                                    // through the variants before showing a raw ID.
                                    const treatmentName =
                                        lt.treatment_data?.treatment_item?.name ||
                                        lt.treatment_data?.name ||
                                        lt.treatment_name ||
                                        lt.name ||
                                        `Treatment #${lt.id}`;
                                    return (
                                        <MenuItem key={lt.id} value={lt.id}>
                                            {treatmentName}
                                            {lt.status && (
                                                <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                                                    ({lt.status})
                                                </Box>
                                            )}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    )}

                    {/* Checklist items */}
                    <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                Checklist Items (optional)
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<Iconify icon="eva:plus-fill" width={16} />}
                                onClick={addChecklistItem}
                                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                            >
                                Add item
                            </Button>
                        </Stack>

                        {checklistItems.length > 0 && (
                            <Stack spacing={1}>
                                {checklistItems.map((item, index) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <Stack key={index} direction="row" spacing={1} alignItems="center">
                                        <TextField
                                            value={item.title}
                                            onChange={(e) => updateChecklistItem(index, 'title', e.target.value)}
                                            placeholder={`Item ${index + 1}`}
                                            size="small"
                                            fullWidth
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={item.is_required}
                                                    onChange={(e) =>
                                                        updateChecklistItem(index, 'is_required', e.target.checked)
                                                    }
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                                                    Required
                                                </Typography>
                                            }
                                            sx={{ ml: 0, mr: 0 }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => removeChecklistItem(index)}
                                            sx={{ color: 'error.main', flexShrink: 0 }}
                                        >
                                            <Iconify icon="eva:trash-2-outline" width={16} />
                                        </IconButton>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                        {checklistItems.length === 0 && (
                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 1 }}>
                                No checklist items yet
                            </Typography>
                        )}
                    </Box>

                    {submitError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {submitError}
                        </Typography>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleClose}
                    disabled={submitting}
                    sx={{ textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    loading={submitting}
                    onClick={handleSubmit(onSubmit)}
                    startIcon={!submitting && <Iconify icon="eva:plus-fill" width={18} />}
                    sx={{ textTransform: 'none' }}
                >
                    Create Task
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

CreateTaskDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onSuccess: PropTypes.func,
    defaultLead: PropTypes.object,
    defaultDue: PropTypes.oneOf(['today', 'week', null]),
    defaultTitle: PropTypes.string,
    defaultDescription: PropTypes.string,
};

CreateTaskDialog.defaultProps = {
    onSuccess: null,
    defaultLead: null,
    defaultDue: null,
    defaultTitle: null,
    defaultDescription: null,
};
